
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseParticipantsDataProps {
  userId: string;
  reagentId?: string;
}

interface UseParticipantsDataReturn {
  username: string;
  reagentName?: string;
  isLoading: boolean;
}

/**
 * Hook to fetch participant data for a conversation (username and reagent name)
 */
export function useParticipantsData({ 
  userId, 
  reagentId 
}: UseParticipantsDataProps): UseParticipantsDataReturn {
  const [username, setUsername] = useState<string>("");
  const [reagentName, setReagentName] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  
  const isMountedRef = useRef(true);

  useEffect(() => {
    const loadProfileAndReagent = async () => {
      if (!userId) return;

      try {
        // Get username from profiles
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("username, display_username")
          .eq("id", userId)
          .single();
          
        if (!profileError && profileData && isMountedRef.current) {
          const displayName = profileData.display_username || profileData.username || "Unknown User";
          setUsername(displayName);
        }
        
        // If reagent ID is provided, fetch the reagent name
        if (reagentId) {
          const { data: reagentData, error: reagentError } = await supabase
            .from("reagents")
            .select("name")
            .eq("id", reagentId)
            .single();
            
          if (!reagentError && reagentData && isMountedRef.current) {
            setReagentName(reagentData.name);
          }
        }
      } catch (err) {
        console.error("Error fetching profile or reagent:", err);
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadProfileAndReagent();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [userId, reagentId]);

  return {
    username,
    reagentName,
    isLoading
  };
}
